import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track, hasConsent } from "@/lib/analytics";
import { PRICING } from "@/content/site";

const PLAN_VALUE = { monthly: Number(PRICING.monthly.slice(1)), annual: Number(PRICING.annual.slice(1)) };

// Backend marks the paid transaction as reported exactly once, so reloads never double-count.
const reportPurchase = async (sessionId) => {
  if (!hasConsent()) return;
  try {
    const r = await api.post("/payments/analytics-claim", { session_id: sessionId });
    if (r.data.first) {
      track("subscription_purchased", { plan_interval: r.data.plan, value: PLAN_VALUE[r.data.plan] ?? null, currency: "USD" });
    }
  } catch (e) {}
};

const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refreshEntitlements } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | success | error
  const polls = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let timer;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          await refreshEntitlements();
          reportPurchase(sessionId);
          setStatus("success");
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch (e) {
        if (polls.current >= MAX_POLLS) {
          setStatus("error");
          return;
        }
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) {
        setStatus("error");
        return;
      }
      timer = setTimeout(poll, 1800);
    };
    poll();
    return () => clearTimeout(timer);
  }, [refreshEntitlements]);

  return (
    <div className="flex min-h-[72vh] flex-col items-center justify-center px-6 text-center">
      {status === "checking" && (
        <div data-testid="payment-checking" className="flex flex-col items-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#D9A441]" />
          <p className="mt-6 font-display text-2xl text-[#F8F3E6]">
            Confirming your subscription…
          </p>
          <p className="mt-1 text-sm text-[#A3A5B8]">This takes just a moment.</p>
        </div>
      )}

      {status === "success" && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6 }}
          data-testid="payment-success"
          className="flex max-w-md flex-col items-center rounded-3xl border border-[#D9A441]/30 bg-[#131422]/70 p-10 backdrop-blur-xl"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#D9A441]/50 bg-[#0B0C15]">
            <Crown className="h-7 w-7 text-[#D9A441]" />
          </span>
          <h1 className="mt-6 font-display text-3xl font-semibold text-[#F8F3E6]">
            Welcome to Premium ✦
          </h1>
          <p className="mt-3 text-[#A3A5B8]">
            You now have <span className="text-[#E5C365]">12 full readings</span> every month,
            plus a Daily Ritual and up to 3 follow-up questions per reading.
          </p>
          <button
            onClick={() => navigate("/")}
            data-testid="start-reading-btn"
            className="mt-8 flex items-center gap-2.5 rounded-full border border-[#D9A441]/60 bg-[#D9A441] px-8 py-3.5 font-medium text-[#0B0C15] transition-all hover:bg-[#E5C365] hover:shadow-[0_0_28px_rgba(217,164,65,0.4)]"
          >
            <Sparkles className="h-5 w-5" /> Start a reading
          </button>
        </motion.div>
      )}

      {status === "error" && (
        <div
          data-testid="payment-error"
          className="flex max-w-md flex-col items-center rounded-3xl border border-[#D9A441]/20 bg-[#131422]/70 p-10"
        >
          <AlertTriangle className="h-10 w-10 text-[#D9A441]" />
          <h1 className="mt-6 font-display text-2xl text-[#F8F3E6]">
            We couldn't confirm your payment
          </h1>
          <p className="mt-3 text-sm text-[#A3A5B8]">
            If you completed checkout, your Premium access will activate shortly. Otherwise, please
            try again.
          </p>
          <button
            onClick={() => navigate("/")}
            data-testid="back-home-btn"
            className="mt-8 rounded-full border border-[#D9A441]/50 bg-[#D9A441]/10 px-6 py-3 text-sm font-medium text-[#E5C365] transition-all hover:bg-[#D9A441]/20"
          >
            Back to The Cardlight
          </button>
        </div>
      )}
    </div>
  );
}
